import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSalesDocuments, useSalesDocument, useCreateSalesDocument } from '@/hooks/use-sales';
import { useClients, useUpdateClient } from '@/hooks/use-clients';
import { calculateDocumentTotals, calculateLineTotals } from '@erp/domain/sales';
import { useCompanySettings } from '@/hooks/use-settings';
import type { Client } from '@erp/data-access/interfaces';
import {
  Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox,
  Input, Label, Separator,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@erp/ui';
import { Minus, Plus, Search } from 'lucide-react';

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function formatDate(d: string): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

interface CreditNoteFormProps {
  sourceDocId?: string;
  onSaved: () => void;
  onCancel: () => void;
}

export function CreditNoteForm({ sourceDocId: initialSourceId, onSaved, onCancel }: CreditNoteFormProps) {
  const { t } = useTranslation();
  const { data: clients } = useClients();
  const { data: companySettings } = useCompanySettings();
  const createMutation = useCreateSalesDocument();
  const updateClientMutation = useUpdateClient();

  // Step 1: Select source document (if not provided)
  const [selectedDocId, setSelectedDocId] = useState(initialSourceId ?? '');
  const [searchDoc, setSearchDoc] = useState('');

  // Load invoices and deliveries for the picker
  const { data: invoices } = useSalesDocuments({ docType: 'invoice' });
  const { data: deliveries } = useSalesDocuments({ docType: 'delivery' });

  // Merge and filter eligible documents
  const eligibleDocs = useMemo(() => {
    const all = [...(invoices ?? []), ...(deliveries ?? [])];
    if (!searchDoc) return all;
    const s = searchDoc.toLowerCase();
    return all.filter((d) => {
      const client = clients?.find((c: Client) => c.id === d.clientId);
      return d.number.toLowerCase().includes(s) || client?.name.toLowerCase().includes(s);
    });
  }, [invoices, deliveries, searchDoc, clients]);

  // Step 2: Load source document details
  const { data: sourceDoc, isLoading: sourceLoading } = useSalesDocument(selectedDocId);

  // Step 3: Select returned lines
  const [selectedLines, setSelectedLines] = useState<Map<number, number>>(new Map());
  const [notes, setNotes] = useState('');
  const [carRegistrationNumber, setCarRegistrationNumber] = useState('');

  const client = useMemo(
    () => clients?.find((c: Client) => c.id === sourceDoc?.clientId),
    [clients, sourceDoc],
  );

  useEffect(() => {
    if (!sourceDoc || companySettings?.showClientCarNumber === false) return;
    setCarRegistrationNumber(sourceDoc.carRegistrationNumber ?? client?.carRegistrationNumber ?? '');
  }, [client?.carRegistrationNumber, companySettings?.showClientCarNumber, sourceDoc]);

  const toggleLine = (index: number, maxQty: number) => {
    setSelectedLines((prev) => {
      const next = new Map(prev);
      if (next.has(index)) next.delete(index);
      else next.set(index, maxQty);
      return next;
    });
  };

  const setReturnQty = (index: number, qty: number) => {
    setSelectedLines((prev) => {
      const next = new Map(prev);
      if (qty <= 0) next.delete(index);
      else next.set(index, qty);
      return next;
    });
  };

  const withholdingConfig = useMemo(() => ({
    threshold: companySettings?.withholdingThreshold ?? 1000,
    rate: companySettings?.withholdingRate ?? 1,
  }), [companySettings]);

  const selectedTotals = useMemo(() => {
    if (!sourceDoc?.lines) return { totalHt: 0, totalVat: 0, totalTtc: 0, netToPay: 0, totalDiscount: 0, withholdingAmount: 0 };
    const lineInputs = Array.from(selectedLines.entries()).map(([idx, qty]) => {
      const line = sourceDoc.lines![idx]!;
      return { quantity: qty, unitPriceHt: line.unitPriceHt, discountPct: line.discountPct, vatRate: line.vatRate };
    });
    return calculateDocumentTotals(lineInputs, client?.type ?? 'individual', withholdingConfig);
  }, [selectedLines, sourceDoc, client, withholdingConfig]);

  const handleCreate = async () => {
    if (!sourceDoc || selectedLines.size === 0) return;
    try {
      const lines = Array.from(selectedLines.entries()).map(([idx, qty]) => {
        const line = sourceDoc.lines![idx]!;
        const lineInput = { quantity: qty, unitPriceHt: line.unitPriceHt, discountPct: line.discountPct, vatRate: line.vatRate };
        const lt = calculateLineTotals(lineInput);
        return { articleId: line.articleId ?? undefined, description: line.description, ...lineInput, totalHt: lt.totalHt, totalTtc: lt.totalTtc };
      });
      const docTotals = calculateDocumentTotals(lines, client?.type ?? 'individual', withholdingConfig);
      await createMutation.mutateAsync({
        docType: 'credit_note',
        clientId: sourceDoc.clientId,
        representativeId: sourceDoc.representativeId ?? null,
        carRegistrationNumber: companySettings?.showClientCarNumber === false
          ? undefined
          : (carRegistrationNumber.trim() || null),
        docDate: todayISO(),
        parentDocId: sourceDoc.id,
        parentDocIds: [sourceDoc.id],
        notes: notes || undefined,
        lines,
        ...docTotals,
      });
      const cleanedCarNumber = carRegistrationNumber.trim();
      if (
        companySettings?.showClientCarNumber !== false &&
        cleanedCarNumber &&
        client &&
        client.carRegistrationNumber !== cleanedCarNumber
      ) {
        await updateClientMutation.mutateAsync({
          id: client.id,
          input: { carRegistrationNumber: cleanedCarNumber },
        });
      }
      onSaved();
    } catch {
      // mutation error is handled by TanStack Query
    }
  };

  const getClientName = (clientId: string) => clients?.find((c: Client) => c.id === clientId)?.name ?? '—';

  // ── Step 1: Select source document ──
  if (!selectedDocId) {
    return (
      <div className="space-y-4 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('sales.creditNoteForm.selectSource')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative md:max-w-sm">
              <Search className="absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" style={{insetInlineStart:"0.6em"}} size={14} />
              <Input placeholder={t('common.search')} value={searchDoc} onChange={(e) => setSearchDoc(e.target.value)} className="ps-8 h-10 md:h-8" />
            </div>

            {/* Mobile: card list */}
            <div className="md:hidden space-y-2 max-h-[60vh] overflow-y-auto">
              {eligibleDocs.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">{t('common.noResults')}</div>
              ) : eligibleDocs.map((doc) => (
                <Button
                  key={doc.id}
                  type="button"
                  variant="ghost"
                  className="w-full h-auto flex-col items-stretch justify-start whitespace-normal text-start rounded-lg border bg-card px-3 py-3 hover:bg-accent active:bg-accent"
                  onClick={() => { setSelectedDocId(doc.id); setSelectedLines(new Map()); }}
                >
                  <div className="flex items-center gap-2 min-w-0 w-full">
                    <Badge variant="outline" className="shrink-0">{doc.docType === 'invoice' ? t('sales.invoice') : t('sales.delivery')}</Badge>
                    <span className="font-medium text-sm truncate">{doc.number}</span>
                    <span className="ms-auto text-sm font-semibold whitespace-nowrap">{formatPrice(doc.totalTtc)} DT</span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground w-full">
                    <span className="truncate">{getClientName(doc.clientId)}</span>
                    <span className="ms-auto whitespace-nowrap">{formatDate(doc.docDate)}</span>
                  </div>
                </Button>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-lg border max-h-[400px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('dashboard.type')}</TableHead>
                    <TableHead>{t('sales.number')}</TableHead>
                    <TableHead>{t('sales.client')}</TableHead>
                    <TableHead>{t('sales.docDate')}</TableHead>
                    <TableHead className="text-end">{t('sales.totalTtc')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eligibleDocs.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">{t('common.noResults')}</TableCell></TableRow>
                  ) : eligibleDocs.map((doc) => (
                    <TableRow key={doc.id} className="cursor-pointer hover:bg-accent" onClick={() => { setSelectedDocId(doc.id); setSelectedLines(new Map()); }}>
                      <TableCell>
                        <Badge variant="outline">{doc.docType === 'invoice' ? t('sales.invoice') : t('sales.delivery')}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{doc.number}</TableCell>
                      <TableCell>{getClientName(doc.clientId)}</TableCell>
                      <TableCell>{formatDate(doc.docDate)}</TableCell>
                      <TableCell className="text-end">{formatPrice(doc.totalTtc)} DT</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="hidden md:flex justify-end">
          <Button variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
        </div>
        <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <Button variant="outline" className="w-full h-11" onClick={onCancel}>{t('common.cancel')}</Button>
        </div>
      </div>
    );
  }

  // ── Loading source document ──
  if (sourceLoading || !sourceDoc) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.loading')}</div>;
  }

  const sourceLines = sourceDoc.lines ?? [];

  // ── Step 2: Select returned articles ──
  return (
    <div className="space-y-4 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0">
      {/* Source document info */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('sales.creditNoteForm.sourceDocument')}</CardTitle>
            {!initialSourceId && (
              <Button variant="ghost" size="sm" onClick={() => { setSelectedDocId(''); setSelectedLines(new Map()); }}>
                {t('sales.creditNoteForm.changeSource')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Row 1: badge, number, amount */}
          <div className="flex items-center gap-2 text-sm min-w-0">
            <Badge variant="outline" className="shrink-0">{sourceDoc.docType === 'invoice' ? t('sales.invoice') : t('sales.delivery')}</Badge>
            <span className="font-medium truncate">{sourceDoc.number}</span>
            <span className="ms-auto font-medium whitespace-nowrap">{formatPrice(sourceDoc.totalTtc)} DT</span>
          </div>
          {/* Row 2: client + date */}
          <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground min-w-0">
            <span className="truncate">{client?.name ?? '—'}</span>
            <span className="ms-auto whitespace-nowrap">{formatDate(sourceDoc.docDate)}</span>
          </div>
        </CardContent>
      </Card>

      {companySettings?.showClientCarNumber !== false && (
        <Card>
          <CardContent className="pt-4">
            <div className="space-y-1.5">
              <Label htmlFor="creditNoteCarRegistration">{t('sales.carRegistrationNumber')}</Label>
              <Input
                id="creditNoteCarRegistration"
                value={carRegistrationNumber}
                onChange={(e) => setCarRegistrationNumber(e.target.value)}
                placeholder={t('sales.carRegistrationPlaceholder')}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Select articles to return */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">{t('sales.creditNoteForm.selectReturned')}</CardTitle>
        </CardHeader>
        <CardContent>
          {/* ── MOBILE: stacked cards ── */}
          <div className="md:hidden space-y-2">
            {sourceLines.map((line, idx) => {
              const isSelected = selectedLines.has(idx);
              const returnQty = selectedLines.get(idx) ?? 0;
              const lineHt = Math.round(returnQty * line.unitPriceHt * (1 - line.discountPct / 100) * 1000) / 1000;

              return (
                <div
                  key={idx}
                  className={`rounded-lg border p-3 transition-colors ${isSelected ? 'bg-primary/5 border-primary/30' : 'bg-card'}`}
                  onClick={() => !isSelected && toggleLine(idx, line.quantity)}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleLine(idx, line.quantity)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-0.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{line.description}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        <span>{t('sales.creditNoteForm.originalQty')}: <span className="text-foreground font-medium">{line.quantity}</span></span>
                        <span>{t('sales.unitPrice')}: <span className="text-foreground">{formatPrice(line.unitPriceHt)} DT</span></span>
                      </div>
                    </div>
                  </div>

                  {isSelected && (
                    <div className="mt-3 pt-3 border-t space-y-2" onClick={(e) => e.stopPropagation()}>
                      <Label className="text-xs text-muted-foreground font-normal">{t('sales.creditNoteForm.returnQty')}</Label>
                      <div className="flex items-center gap-1">
                        <Button type="button" variant="outline" size="icon-sm" className="h-10 w-10 shrink-0"
                          onClick={() => setReturnQty(idx, Math.max(0, returnQty - 1))}><Minus size={14} /></Button>
                        <Input type="number" inputMode="numeric" min="1" max={line.quantity} step="1" value={returnQty}
                          onChange={(e) => setReturnQty(idx, Math.min(line.quantity, Math.max(0, Number(e.target.value))))}
                          className="h-10 flex-1 min-w-0 text-center text-sm" />
                        <Button type="button" variant="outline" size="icon-sm" className="h-10 w-10 shrink-0"
                          onClick={() => setReturnQty(idx, Math.min(line.quantity, returnQty + 1))}><Plus size={14} /></Button>
                      </div>
                      <div className="flex justify-between items-center pt-1 text-sm">
                        <span className="text-muted-foreground text-xs uppercase tracking-wide">{t('sales.lineTotal')}</span>
                        <span className="font-semibold">{formatPrice(lineHt)} DT</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── DESKTOP: table ── */}
          <div className="hidden md:block divide-y rounded-lg border">
            <div className="grid grid-cols-[auto_2fr_1fr_1.5fr_1fr_1fr] gap-3 text-[11px] font-medium text-muted-foreground px-3 py-2 bg-muted/50">
              <div className="w-6" />
              <div>{t('sales.description')}</div>
              <div className="text-center">{t('sales.creditNoteForm.originalQty')}</div>
              <div className="text-center">{t('sales.creditNoteForm.returnQty')}</div>
              <div className="text-end">{t('sales.unitPrice')}</div>
              <div className="text-end">{t('sales.lineTotal')}</div>
            </div>

            {sourceLines.map((line, idx) => {
              const isSelected = selectedLines.has(idx);
              const returnQty = selectedLines.get(idx) ?? 0;
              const lineHt = Math.round(returnQty * line.unitPriceHt * (1 - line.discountPct / 100) * 1000) / 1000;

              return (
                <div
                  key={idx}
                  className={`grid grid-cols-[auto_2fr_1fr_1.5fr_1fr_1fr] gap-3 items-center px-3 py-3 cursor-pointer transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-accent/50'}`}
                  onClick={() => toggleLine(idx, line.quantity)}
                >
                  <div className="w-6">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleLine(idx, line.quantity)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="text-sm font-medium">{line.description}</div>
                  <div className="text-center text-sm text-muted-foreground">{line.quantity}</div>
                  <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {isSelected ? (
                      <>
                        <Button type="button" variant="outline" size="icon-xs" className="h-7 w-6"
                          onClick={() => setReturnQty(idx, Math.max(0, returnQty - 1))}><Minus size={10} /></Button>
                        <Input type="number" min="1" max={line.quantity} step="1" value={returnQty}
                          onChange={(e) => setReturnQty(idx, Math.min(line.quantity, Math.max(0, Number(e.target.value))))}
                          className="h-7 w-14 text-center text-sm" />
                        <Button type="button" variant="outline" size="icon-xs" className="h-7 w-6"
                          onClick={() => setReturnQty(idx, Math.min(line.quantity, returnQty + 1))}><Plus size={10} /></Button>
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </div>
                  <div className="text-end text-sm">{formatPrice(line.unitPriceHt)} DT</div>
                  <div className="text-end text-sm font-medium">{isSelected ? `${formatPrice(lineHt)} DT` : '—'}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Totals + Notes */}
      <div className="flex flex-col-reverse md:flex-row gap-4">
        <div className="flex-1 space-y-3">
          <div className="space-y-1.5">
            <Label>{t('sales.notes')}</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('sales.creditNoteForm.reason')} className="h-10 md:h-8" />
          </div>
        </div>
        <Card className="w-full md:w-72 shrink-0">
          <CardContent className="pt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('sales.totalHt')}</span>
              {formatPrice(selectedTotals.totalHt)} DT
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('sales.totalVat')}</span>
              {formatPrice(selectedTotals.totalVat)} DT
            </div>
            <Separator />
            <div className="flex justify-between font-bold">
              <span>{t('sales.creditNoteForm.refundAmount')}</span>
              {formatPrice(selectedTotals.totalTtc)} DT
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Desktop actions */}
      <div className="hidden md:flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button onClick={handleCreate} disabled={selectedLines.size === 0 || createMutation.isPending}>
          {createMutation.isPending ? t('common.loading') : t('sales.creditNoteForm.create')}
        </Button>
      </div>
      {/* Mobile sticky action bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex gap-2">
        <Button variant="outline" className="flex-1 h-11" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button className="flex-[2] h-11" onClick={handleCreate} disabled={selectedLines.size === 0 || createMutation.isPending}>
          {createMutation.isPending ? t('common.loading') : t('sales.creditNoteForm.create')}
        </Button>
      </div>
    </div>
  );
}
